export const formatDescription = (description) => {
  if (!description) return null;

  // Remove múltiplos espaços e limpa
  const cleaned = description.replace(/\s+/g, " ").trim();

  // Quebra por pontos seguidos de espaço e palavra com primeira letra maiúscula
  const parts = cleaned.replace(/\.\s+([A-Z][a-z])/g, ".\n$1").split("\n");
  

  return parts
    .map((part, index) => {
      const trimmedPart = part.trim();
      if (!trimmedPart) return null;

      // Detecta padrão "Campo: Valor"
      const fieldMatch = trimmedPart.match(/^([^:]+):\s*(.+)$/);

      if (fieldMatch) {
        const [, label, value] = fieldMatch;
          return (
            <div key={index} className="description-field">
              <span className="field-label">{label.trim()}: </span>
              <span className="field-value text-black">{value.trim()}</span>
            </div>
          );
      }

      return (
        <div key={index} className="description-line">
          {trimmedPart}
        </div>
      );
    })
    .filter(Boolean);
};

export const formatDescriptionHybrid = (description) => {
  
  if (!description) return null;
  // Tenta detectar se já tem quebras de linha
  if (description.includes("\n")) {
    // Já formatado, apenas processa
    return description
      .split("\n")
      .map((line, index) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return null;
        
        const fieldMatch = trimmedLine.match(/^([^:]+):\s*(.+)$/);

        if (fieldMatch) {
          const [, label, value] = fieldMatch;

          return (
            <div key={index} className="description-field">
              <span className="field-label">{label.trim()}:</span>
              <span className="field-value">{value.trim()}</span>
            </div>
          );
        }
        
        return (
          <div key={index} className="description-line">
            {trimmedLine}
          </div>
        );
      })
      .filter(Boolean);
  }

  // Senão, usa formatação automática
  return formatDescription(description);
};

/**
 * Utilitários para o componente Calendar
 */

/**
 * Formata um valor monetário para exibição
 * @param {number|string} value - Valor a ser formatado
 * @returns {string} - Valor formatado como moeda brasileira
 */
export const formatCurrency = (value) => {
  if (!value && value !== 0) return "R$ 0,00";

  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(numericValue);
};

/**
 * Formata a duração em minutos para uma string legível
 * @param {number} minutes - Duração em minutos
 * @returns {string} - Duração formatada
 */
export const formatDuration = (minutes) => {
  if (!minutes) return "";

  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}min`;
};

/**
 * Calcula a cor de contraste ideal (branca ou preta) para uma cor de fundo
 * @param {string} backgroundColor - Cor de fundo em hexadecimal
 * @returns {string} - '#ffffff' ou '#000000'
 */
export const getContrastColor = (backgroundColor) => {
  if (!backgroundColor) return "#000000";

  // Remove o # se presente
  const hex = backgroundColor.replace("#", "");

  // Converte para RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calcula a luminância
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Retorna branco para cores escuras e preto para cores claras
  return luminance > 0.5 ? "#000000" : "#ffffff";
};

/**
 * Gera uma cor aleatória em hexadecimal
 * @returns {string} - Cor em formato hexadecimal
 */
export const generateRandomColor = () => {
  const colors = [
    "#3498db",
    "#2ecc71",
    "#e74c3c",
    "#9b59b6",
    "#f1c40f",
    "#1abc9c",
    "#e67e22",
    "#34495e",
    "#d35400",
    "#8e44ad",
    "#16a085",
    "#27ae60",
    "#2980b9",
    "#8e44ad",
    "#2c3e50",
    "#f39c12",
    "#e67e22",
    "#d35400",
    "#c0392b",
    "#8e44ad",
  ];

  return colors[Math.floor(Math.random() * colors.length)];
};

/**
 * Valida se uma cor hexadecimal é válida
 * @param {string} color - Cor a ser validada
 * @returns {boolean} - true se válida, false caso contrário
 */
export const isValidHexColor = (color) => {
  if (!color) return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

/**
 * Escurece ou clareia uma cor
 * @param {string} color - Cor em hexadecimal
 * @param {number} percent - Porcentagem para escurecer (negativo) ou clarear (positivo)
 * @returns {string} - Nova cor em hexadecimal
 */
export const adjustColorBrightness = (color, percent) => {
  if (!isValidHexColor(color)) return color;

  const hex = color.replace("#", "");
  const num = parseInt(hex, 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;

  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

/**
 * Converte uma data/hora para o formato do FullCalendar
 * @param {Date|string} date - Data a ser convertida
 * @returns {string} - Data no formato ISO sem timezone
 */
export const formatDateForCalendar = (date) => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  // Formato YYYY-MM-DDTHH:mm:ss sem timezone
  return dateObj.toISOString().slice(0, 19);
};

/**
 * Calcula a diferença em minutos entre duas datas
 * @param {Date|string} startDate - Data de início
 * @param {Date|string} endDate - Data de fim
 * @returns {number} - Diferença em minutos
 */
export const getEventDurationInMinutes = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  return Math.abs(end.getTime() - start.getTime()) / (1000 * 60);
};

/**
 * Verifica se um evento está acontecendo agora
 * @param {Date|string} startDate - Data de início do evento
 * @param {Date|string} endDate - Data de fim do evento
 * @returns {boolean} - true se o evento está acontecendo agora
 */
export const isEventHappeningNow = (startDate, endDate) => {
  if (!startDate || !endDate) return false;

  const now = new Date();
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  return now >= start && now <= end;
};

/**
 * Trunca um texto para um número máximo de caracteres
 * @param {string} text - Texto a ser truncado
 * @param {number} maxLength - Número máximo de caracteres
 * @returns {string} - Texto truncado com "..." se necessário
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};
